import { gridReducer, createInitialGrid } from '../grid/gridReducer'
import { loadGrid, saveGrid } from '../grid/gridStorage'
import { pushHistory, snapshot } from './locationHistory'
import { loadJSON, loadRaw, saveJSON, saveRaw } from '../../utils/tabStorage'
import { standardId } from '../../utils/standardRef'

// Must match the code tab's grid and per-window nav stores. The search pages
// write these before navigating, because the code page is unmounted there.
export const CODE_GRID_KEY = 'ct-grid-code-v1'
export const CODE_NAV_KEY = 'ct-code-nav-v2'
export const CODE_WINDOW_LIMITS = { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 }

const JUMP_CURSOR_KEY = 'ct-code-jump-cursor'

function readCursor() {
  const n = Number(loadRaw(JUMP_CURSOR_KEY))
  return Number.isInteger(n) && n >= 0 ? n : 0
}

function writeCursor(n) {
  saveRaw(JUMP_CURSOR_KEY, String(n))
}

function loadNavMap() {
  const parsed = loadJSON(CODE_NAV_KEY)
  return parsed && typeof parsed === 'object' ? parsed : {}
}

function saveNavMap(map) {
  saveJSON(CODE_NAV_KEY, map)
}

function windowList(state) {
  const list = []
  ;(state?.columns || []).forEach((col) => col.windows.forEach((win) => list.push(win)))
  return list
}

// Under the cap, append a code window and focus it. At the cap, reuse windows
// in layout order (left to right, top to bottom), advancing one slot per call.
export function assignCodeWindow(state, cursor, limits = CODE_WINDOW_LIMITS) {
  const windows = windowList(state || { columns: [] })
  // No code grid yet: the first window is the one this jump opens.
  if (!windows.length) {
    const next = createInitialGrid('code')
    return { state: next, windowId: next.activeWindowId, cursor }
  }
  if (windows.length < limits.maxWindows) {
    const next = gridReducer(state, {
      type: 'ADD_WINDOW',
      entityType: 'code',
      entityId: null,
      limits,
    })
    return { state: next, windowId: next.activeWindowId, cursor }
  }
  const index = windows.length ? (Number(cursor) || 0) % windows.length : 0
  const windowId = windows[index].id
  let next = state
  if (windows[index].minimized) {
    next = gridReducer(next, { type: 'RESTORE', id: windowId })
  }
  next = gridReducer(next, { type: 'FOCUS', id: windowId })
  next = { ...next, activeWindowId: windowId, soloWindowId: null }
  return { state: next, windowId, cursor: index + 1 }
}

// Point a window's nav at `item`: open its PDF, enter its standard, and record
// the visit. `path` is the ancestor orders (root first); drilling to the parent
// leaves the section as a visible row.
export function navForJump(raw, item) {
  const snap = snapshot(item)
  if (!snap) return raw && typeof raw === 'object' ? raw : null
  const existing = raw && typeof raw === 'object' ? raw : {}
  const mode = existing.mode === 'collapsed' ? 'collapsed' : 'segmented'
  const path = Array.isArray(snap.path) ? snap.path : []
  const collapsed = new Set(Array.isArray(existing.collapsed) ? existing.collapsed : [])
  path.forEach((order) => collapsed.delete(order))
  const next = {
    mode,
    standard: standardId(snap.standard) || null,
    trailOrders: mode === 'collapsed' ? [] : path.slice(1),
    collapsed: [...collapsed],
    selectedId: snap._id,
    open: snap,
    history: Array.isArray(existing.history) ? existing.history : [],
    historyIndex: Number.isInteger(existing.historyIndex) ? existing.historyIndex : -1,
    filter: existing.filter,
  }
  return pushHistory(next, snap)
}

// Persist the jump and return the window id that will show it. The caller
// navigates to the code tab, which reloads both stores on mount.
export function openItemInCode(item) {
  const snap = snapshot(item)
  if (!snap || typeof window === 'undefined') return null
  const assigned = assignCodeWindow(loadGrid(CODE_GRID_KEY), readCursor())
  const nav = loadNavMap()
  nav[assigned.windowId] = navForJump(nav[assigned.windowId], snap)
  saveNavMap(nav)
  saveGrid(CODE_GRID_KEY, assigned.state)
  writeCursor(assigned.cursor)
  return assigned.windowId
}
